/**
 * Shared admin alerts: sound, browser notification, vibration.
 * Used by Orders page and global AdminLayout live-alert polling.
 */

export const LS_ADMIN_ORDERS_SOUND = 'nannyb-admin-orders-sound-enabled';

export function isAdminSoundEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const v = window.localStorage.getItem(LS_ADMIN_ORDERS_SOUND);
    if (v === null) return true;
    return v === '1';
  } catch {
    return true;
  }
}

/** Persist looping alert sound preference; stops active loops when turned off. */
export function setAdminSoundEnabled(enabled: boolean) {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(LS_ADMIN_ORDERS_SOUND, enabled ? '1' : '0');
    } catch {
      /* ignore */
    }
  }
  if (!enabled) {
    stopAllAlertSoundLoops();
  }
}

/** Short vibration when supported (Android — helps if speaker is muted). */
export function pulseDeviceIfSupported() {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([100, 60, 100, 60, 100]);
    }
  } catch {
    /* ignore */
  }
}

/** Stop device vibration when supported. */
export function stopDeviceVibration() {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(0);
    }
  } catch {
    /* ignore */
  }
}

/** ~Duration of one order alarm cycle (seconds). */
const ORDER_SOUND_CYCLE_S = 1.14;
/** ~Duration of one message chime cycle (seconds). */
const MESSAGE_SOUND_CYCLE_S = 0.75;
/** Pause between sound repeats while alert is open. */
const ALERT_SOUND_LOOP_GAP_MS = 450;

type AlertSoundKind = 'order' | 'message';

const activeSoundLoops: Record<AlertSoundKind, (() => void) | null> = {
  order: null,
  message: null,
};

function stopAlertSoundLoop(kind: AlertSoundKind) {
  activeSoundLoops[kind]?.();
  activeSoundLoops[kind] = null;
  stopDeviceVibration();
}

/** Stop looping alert sound for one kind (called when Tammy dismisses that popup). */
export function stopOrderAlertSoundLoop() {
  stopAlertSoundLoop('order');
}

export function stopMessageAlertSoundLoop() {
  stopAlertSoundLoop('message');
}

/** Stop all looping alert sounds (e.g. on unmount). */
export function stopAllAlertSoundLoops() {
  stopAlertSoundLoop('order');
  stopAlertSoundLoop('message');
}

/**
 * Schedule `playOnce` on a timer until `stop()` is called.
 * Exported for unit tests — powers looping order/message alerts.
 */
export function createRepeatingSoundLoop(
  playOnce: () => void,
  cycleMs: number,
  gapMs: number = ALERT_SOUND_LOOP_GAP_MS,
): () => void {
  let cancelled = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const tick = () => {
    if (cancelled) return;
    playOnce();
    timeoutId = setTimeout(tick, cycleMs + gapMs);
  };

  tick();

  return () => {
    cancelled = true;
    if (timeoutId) clearTimeout(timeoutId);
  };
}

/**
 * Repeat order/message alert sound until `stop*` is called (Tammy dismisses the popup).
 * Returns the same stop handle stored internally — safe to call twice.
 */
function startAlertSoundLoop(
  kind: AlertSoundKind,
  audioContext: AudioContext | null,
  playOnce: (ctx: AudioContext | null) => void,
  cycleSeconds: number,
): () => void {
  stopAlertSoundLoop(kind);
  if (!audioContext) {
    return () => stopAlertSoundLoop(kind);
  }

  const stopRepeat = createRepeatingSoundLoop(
    () => playOnce(audioContext),
    cycleSeconds * 1000,
  );

  const stop = () => {
    stopRepeat();
    if (activeSoundLoops[kind] === stop) {
      activeSoundLoops[kind] = null;
    }
    stopDeviceVibration();
  };

  activeSoundLoops[kind] = stop;
  return stop;
}

/** Loop order alarm until dismissed. */
export function startOrderAlertSoundLoop(audioContext: AudioContext | null): () => void {
  return startAlertSoundLoop('order', audioContext, playNotificationSound, ORDER_SOUND_CYCLE_S);
}

/** Loop message chime until dismissed. */
export function startMessageAlertSoundLoop(audioContext: AudioContext | null): () => void {
  return startAlertSoundLoop('message', audioContext, playMessageNotificationSound, MESSAGE_SOUND_CYCLE_S);
}

/** Sharper multi-tone alarm — needs a resumed AudioContext from user gesture. */
export function playNotificationSound(audioContext: AudioContext | null) {
  if (!audioContext) return;
  const run = () => {
    try {
      pulseDeviceIfSupported();
      const ctx = audioContext;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sawtooth';
      const g = gainNode.gain;
      const t0 = ctx.currentTime + 0.02;
      const chirps: Array<{ start: number; peak: number; pitch: number }> = [
        { start: 0.0, peak: 0.22, pitch: 1120 },
        { start: 0.28, peak: 0.5, pitch: 760 },
        { start: 0.56, peak: 0.78, pitch: 1120 },
        { start: 0.84, peak: 1.06, pitch: 760 },
      ];
      oscillator.frequency.setValueAtTime(chirps[0].pitch, t0);
      g.setValueAtTime(0, t0);
      for (const chirp of chirps) {
        const tStart = t0 + chirp.start;
        const tPeak = t0 + chirp.peak;
        oscillator.frequency.setValueAtTime(chirp.pitch, tStart);
        oscillator.frequency.linearRampToValueAtTime(chirp.pitch + 140, tPeak);
        g.linearRampToValueAtTime(0.42, tStart + 0.02);
        g.linearRampToValueAtTime(0, tPeak);
      }
      g.setValueAtTime(0, t0 + 1.12);
      oscillator.start(t0);
      oscillator.stop(t0 + 1.14);
    } catch (e) {
      console.warn('Notification sound failed:', e);
    }
  };
  if (audioContext.state === 'suspended') {
    audioContext.resume().then(run).catch(() => {});
  } else {
    run();
  }
}

/** Two-tone chime for contact messages (distinct from order alarm). */
export function playMessageNotificationSound(audioContext: AudioContext | null) {
  if (!audioContext) return;
  const run = () => {
    try {
      pulseDeviceIfSupported();
      const ctx = audioContext;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      const t0 = ctx.currentTime + 0.02;
      osc.frequency.setValueAtTime(880, t0);
      osc.frequency.setValueAtTime(1175, t0 + 0.18);
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.35, t0 + 0.04);
      gain.gain.linearRampToValueAtTime(0, t0 + 0.35);
      gain.gain.linearRampToValueAtTime(0.35, t0 + 0.4);
      gain.gain.linearRampToValueAtTime(0, t0 + 0.72);
      osc.start(t0);
      osc.stop(t0 + 0.75);
    } catch (e) {
      console.warn('Message notification sound failed:', e);
    }
  };
  if (audioContext.state === 'suspended') {
    audioContext.resume().then(run).catch(() => {});
  } else {
    run();
  }
}

export async function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
}

export function showBrowserNotification(
  title: string,
  body: string,
  opts?: { tag?: string; requireInteraction?: boolean },
) {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/assets/images/branding/logo2.webp',
      tag: opts?.tag ?? title,
      requireInteraction: opts?.requireInteraction ?? false,
    });
  }
}

export function unlockAdminAudio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  return ctx;
}
